import React from 'react';
import { Modal, useModal } from '@faceless-ui/modal';
import type { ModalProps as ModalPropsType } from '@faceless-ui/modal/dist/Modal';
import { constructClassName } from '../../utils/constructClassName';
import { Button, Icon } from '..';
import styles from './styles.module.scss';

export interface ModalProps {
  children: React.ReactNode;
  className?: string;
  innerModalClassName?: string;
  modalSize?: 'small' | 'medium' | 'semiLarge' | 'large';
  modalName: string | React.ReactNode;
  description?: string;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  hideCancel?: boolean;
  cancelText?: string;
  confirmText?: string;
}

export const AppModal: React.FC<ModalProps & ModalPropsType> = ({
  slug,
  children,
  className,
  modalSize = 'medium',
  modalName,
  description,
  headerContent,
  footerContent,
  hideHeader,
  hideFooter,
  innerModalClassName,
  onCancel,
  onConfirm,
  hideCancel,
  cancelText,
  confirmText,
  ...rest
}) => {
  const { closeModal } = useModal();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    closeModal(slug);
  };

  return (
    <Modal
      slug={slug}
      className={constructClassName([
        className,
      ])}
      {...rest}
    >
      <div className={constructClassName([
        innerModalClassName,
        styles.modal,
        styles[`${modalSize}Modal`],
      ])}
      >
        <div className={constructClassName([
          styles.container,
        ])}
        >
          {
            !hideHeader && (
              <div className={styles.headContainer}>
                {
                  headerContent || (
                    <React.Fragment>
                      <div>
                        <h3 className={styles.title}>{modalName}</h3>
                        {
                          description && (
                            <p className={styles.description}>{description}</p>
                          )
                        }
                      </div>
                      <Button
                        className={styles.closeButton}
                        onClick={() => closeModal(slug)}
                        type="link"
                        title="Close modal"
                      >
                        <Icon
                          icon="xMarkClassicLight"
                          size={30}
                        />
                      </Button>
                    </React.Fragment>
                  )
                }
              </div>
            )
          }
          <div className={styles.contentContainer}>
            {children}
          </div>
          {
            !hideFooter && (
              <div className={styles.footerContainer}>
                {
                  footerContent || (
                    <React.Fragment>
                      {
                        !hideCancel && (
                          <Button
                            type="secondary"
                            onClick={handleCancel}
                            title="Cancel"
                          >
                            {cancelText || 'Cancel'}
                          </Button>
                        )
                      }
                      <Button
                        type="primary"
                        onClick={onConfirm}
                        title="Confirm"
                      >
                        {confirmText || 'Confirm'}
                      </Button>
                    </React.Fragment>
                  )
                }
              </div>
            )
          }
        </div>
      </div>

    </Modal>
  );
};
